import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  DEFAULT_NOTE_PRIORITY,
  NOTE_FILTER_PRIORITIES,
  NOTE_PRIORITIES,
  noteCreateSchema,
} from '@app/shared';
import type { NoteCreateInput, NoteDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { ConfirmDialog, FormDialog } from '../../components/dialogs';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { formatDateTime } from '../../lib/format';
import { ReleasedFeature, useFeatureReleased } from '../../release';
import { Can } from '../auth/guards';
import { useLookupOptions } from '../configuration/api';
import { withRequiredMessages } from '../inventory/formUtils';
import { useAddNote, useCustomerNotes, useNotePosters, useVoidNote } from './api';
import { HighestPriorityNotes, RetentionNote } from './CustomerPageDialogs';
import { Section } from './parts';

const NOTE_MAX = 1000;
const PAGE_SIZE = 10;

interface NoteForm {
  noteTypeId: string;
  callerId: string;
  text: string;
  priority: (typeof NOTE_PRIORITIES)[number];
}
const EMPTY: NoteForm = {
  noteTypeId: '',
  callerId: '',
  text: '',
  priority: DEFAULT_NOTE_PRIORITY,
};

/** "Add New Note" (PC83): note type, caller ID, the note with its character counter, priority. */
function AddNoteDialog({
  customerId,
  open,
  onClose,
}: {
  customerId: string;
  open: boolean;
  onClose: () => void;
}) {
  const notify = useNotify();
  const noteTypes = useLookupOptions('noteType', open);
  const addNote = useAddNote(customerId);
  const { control, handleSubmit, reset, setError, getValues, setValue } = useForm<NoteForm>({
    defaultValues: EMPTY,
    resolver: withRequiredMessages<NoteForm>(
      zodResolver(noteCreateSchema) as unknown as Resolver<NoteForm>,
      { noteTypeId: 'Select Note Type' },
    ),
  });
  const text = useWatch({ control, name: 'text' });
  const noteTypeId = useWatch({ control, name: 'noteTypeId' });

  // Assumption: the note text kept with a note type (Other Settings > Manage Note Type) is
  // offered as the note when the box is still empty. The reference stores such a text per
  // type; how this form uses it was not observed.
  useEffect(() => {
    const preset = noteTypes.data?.find((t) => t.id === noteTypeId)?.notes;
    if (preset && !getValues('text')) setValue('text', preset);
  }, [noteTypeId, noteTypes.data, getValues, setValue]);

  useEffect(() => {
    if (open) reset(EMPTY);
  }, [open, reset]);

  const submit = handleSubmit(async (values) => {
    try {
      // The resolver hands over the parsed schema output, which is what the API expects.
      await addNote.mutateAsync(values as NoteCreateInput);
      notify.success('Note added.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={open}
      title="Add New Note"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={addNote.isPending}
    >
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <SelectInput
          control={control}
          name="noteTypeId"
          label="Select Note Type"
          required
          options={(noteTypes.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          helperText={noteTypes.isError ? 'Could not load the note types' : undefined}
        />
        <TextInput
          control={control}
          name="callerId"
          label="Caller ID"
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
        />
        <Box sx={{ gridColumn: '1 / -1' }}>
          <TextInput
            control={control}
            name="text"
            label="Note"
            required
            multiline
            minRows={4}
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          />
          <FormHelperText>Characters remaining: {NOTE_MAX - (text?.length ?? 0)}</FormHelperText>
        </Box>
        <SelectInput
          control={control}
          name="priority"
          label="Priority"
          options={toOptions(NOTE_PRIORITIES)}
        />
        {/* As on the reference: the link opens the Ticketing System page in a new tab. */}
        <ReleasedFeature name="note-ticket-link">
          <Can code="PC402">
            <Can code="PC722">
              <Link to="/tickets" target="_blank" rel="noopener">
                Do you want to create a ticket? (PC402)
              </Link>
            </Can>
          </Can>
        </ReleasedFeature>
      </Box>
    </FormDialog>
  );
}

/**
 * Assumption: the reference shows the notes as a list whose columns were not recorded; these
 * are the fields a note has. The columns of its archive page (# | Customer Id | Notes | Note
 * Type | Priority | Posted By | Posted DateTime) are in `ArchivedNotesPage`.
 */
const baseColumns: Column<NoteDto>[] = [
  { key: 'noteType', header: 'Note Type', render: (n) => n.noteType },
  { key: 'text', header: 'Note', render: (n) => n.text },
  { key: 'callerId', header: 'Caller ID', nowrap: true, render: (n) => n.callerId },
  { key: 'priority', header: 'Priority', render: (n) => n.priority },
  { key: 'createdBy', header: 'Added By', render: (n) => n.createdBy?.loginId ?? '' },
  {
    key: 'createdAt',
    header: 'Date Time',
    nowrap: true,
    render: (n) => formatDateTime(n.createdAt),
  },
];

interface Filters {
  q: string;
  postedBy: string;
  priority: string;
}
const NO_FILTERS: Filters = { q: '', postedBy: '', priority: '' };

/**
 * "Display Notes": the reference's "Customer Notes (Customer ID - n)" dialog. Filters: Notes
 * (the words), Posted By and Priority, with Search and Clear Search. Its Ticket Status filter
 * is not offered: notes hang on no ticket here.
 */
function DisplayNotesDialog({
  customerId,
  customerNo,
  open,
  onClose,
}: {
  customerId: string;
  customerNo: number;
  open: boolean;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [applied, setApplied] = useState<Filters>(NO_FILTERS);
  const [page, setPage] = useState(1);
  const posters = useNotePosters(customerId, open);
  const notes = useCustomerNotes(customerId, {
    page,
    pageSize: PAGE_SIZE,
    q: applied.q,
    postedBy: applied.postedBy,
    priority: applied.priority,
  });
  const set = (key: keyof Filters) => (e: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));
  const search = () => {
    setApplied(draft);
    setPage(1);
  };
  const clear = () => {
    setDraft(NO_FILTERS);
    setApplied(NO_FILTERS);
    setPage(1);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Customer Notes (Customer ID - {customerNo})
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          component="form"
          aria-label="Notes filters"
          onSubmit={(e) => {
            e.preventDefault();
            search();
          }}
          sx={{
            display: 'grid',
            gap: 1.5,
            mb: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            alignItems: 'start',
          }}
        >
          <TextField
            size="small"
            label="Notes"
            value={draft.q}
            onChange={set('q')}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            select
            size="small"
            label="Posted By"
            value={draft.postedBy}
            onChange={set('postedBy')}
          >
            <MenuItem value="">Select</MenuItem>
            {(posters.data ?? []).map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.loginId}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Priority"
            value={draft.priority}
            onChange={set('priority')}
          >
            <MenuItem value="">Select</MenuItem>
            {NOTE_FILTER_PRIORITIES.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minHeight: 40 }}>
            <Button type="submit" variant="contained">
              Search
            </Button>
            <Button type="button" variant="outlined" onClick={clear}>
              Clear Search
            </Button>
          </Box>
        </Box>
        <DataTable
          label="Filtered notes"
          columns={baseColumns}
          rows={notes.data?.data}
          rowKey={(n) => n.id}
          loading={notes.isFetching}
          error={notes.error}
          onRetry={() => void notes.refetch()}
          emptyText="No notes match the filters."
          page={page}
          pageSize={PAGE_SIZE}
          total={notes.data?.total ?? 0}
          onPageChange={setPage}
          pageSizeOptions={[PAGE_SIZE]}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Customer Notes" on the profile, newest first, with "Add New Note", "Display Notes" (the
 * filters), "View Archive Notes" and Mark Void (PC248) on each note.
 */
export function NotesSection({
  customerId,
  customerNo,
}: {
  customerId: string;
  customerNo: number;
}) {
  const notify = useNotify();
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [displaying, setDisplaying] = useState(false);
  const [voiding, setVoiding] = useState<NoteDto | null>(null);
  const notes = useCustomerNotes(customerId, { page, pageSize: PAGE_SIZE });
  const voidNote = useVoidNote(customerId);
  const featureOn = useFeatureReleased();

  const confirmVoid = async () => {
    if (!voiding) return;
    try {
      await voidNote.mutateAsync(voiding.id);
      notify.success('The note was marked void and moved to the archive.');
      setVoiding(null);
    } catch (err) {
      notify.error(err);
    }
  };

  // Kept apart so the column goes with its release switch.
  function voidColumn(): Column<NoteDto> {
    return {
      key: 'option',
      header: 'Option',
      nowrap: true,
      render: (n) => (
        <Can code="PC248">
          <Button size="small" color="error" onClick={() => setVoiding(n)}>
            Mark Void (PC248)
          </Button>
        </Can>
      ),
    };
  }
  const columns: Column<NoteDto>[] = [
    ...baseColumns,
    ...(featureOn('notes-mark-void') ? [voidColumn()] : []),
  ];

  return (
    <Section
      title="Customer Notes"
      actions={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ReleasedFeature name="highest-priority-notes">
            <HighestPriorityNotes customerId={customerId} />
          </ReleasedFeature>
          <ReleasedFeature name="retention-note">
            <RetentionNote customerId={customerId} />
          </ReleasedFeature>
          <ReleasedFeature name="notes-display">
            <Button variant="outlined" onClick={() => setDisplaying(true)}>
              Display Notes
            </Button>
          </ReleasedFeature>
          <ReleasedFeature name="notes-mark-void">
            <Button variant="outlined" component={Link} to="notes/archive">
              View Archive Notes
            </Button>
          </ReleasedFeature>
          <Can code="PC83">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
              Add New Note (PC83)
            </Button>
          </Can>
        </Box>
      }
    >
      <DataTable
        label="Customer Notes"
        columns={columns}
        rows={notes.data?.data}
        rowKey={(n) => n.id}
        loading={notes.isFetching}
        error={notes.error}
        onRetry={() => void notes.refetch()}
        emptyText="No notes have been added for this customer."
        page={page}
        pageSize={PAGE_SIZE}
        total={notes.data?.total ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[PAGE_SIZE]}
      />
      <AddNoteDialog customerId={customerId} open={adding} onClose={() => setAdding(false)} />
      <DisplayNotesDialog
        customerId={customerId}
        customerNo={customerNo}
        open={displaying}
        onClose={() => setDisplaying(false)}
      />
      <ConfirmDialog
        open={voiding !== null}
        title="Mark Void"
        message="This note will be marked void and moved to the archive. Do you want to continue?"
        confirmLabel="Mark Void"
        danger
        busy={voidNote.isPending}
        onConfirm={() => void confirmVoid()}
        onClose={() => setVoiding(null)}
      />
    </Section>
  );
}
